import React, { useEffect, useRef, useCallback } from 'react';

// masonry grid with infinite scroll
function MasonryGrid({ images, onLoadMore, hasMore, loading }) {
  const loaderRef = useRef(null);

  // intersection observer callback
  const handleIntersect = useCallback((entries) => {
    const entry = entries[0];
    if (entry.isIntersecting && hasMore && !loading) {
      onLoadMore();
    }
  }, [hasMore, loading, onLoadMore]);

  // setup intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '100px',
      threshold: 0
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [handleIntersect]);

  // format relative time
  const formatTime = (dateString) => {
    const now = Date.now();
    const date = new Date(dateString).getTime();
    const diff = now - date;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  // empty state
  if (images.length === 0) {
    return (
      <div className="text-center py-20 text-gray-400">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
        <p className="text-lg font-medium">No images yet</p>
        <p className="text-sm">Upload some images to get started!</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Your Feed</h2>

      {/* masonry layout */}
      <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4">
        {images.map((image, index) => (
          <div
            key={image.id}
            className="break-inside-avoid mb-4 bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow"
          >
            <img
              src={image.src}
              alt={image.name || 'uploaded image'}
              className="w-full"
              loading="lazy"
            />
            <div className="p-4">
              <h3 className="font-semibold text-gray-800">
                {image.title || `Post #${index + 1}`}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {image.caption || image.name}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {formatTime(image.uploadedAt)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* load more trigger */}
      <div ref={loaderRef} className="text-center py-8">
        {loading && (
          <div className="inline-flex items-center gap-2 text-gray-500">
            <span className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
            Loading more...
          </div>
        )}
        {!hasMore && images.length > 0 && (
          <p className="text-gray-400 text-sm">You've reached the end</p>
        )}
      </div>
    </div>
  );
}

export default MasonryGrid;
